<template>
  <div class="registration">
    <div class="registration-container">
      <!-- Left Panel -->
      <div class="left-panel">
        <div class="header">
          <h2><i class="mdi mdi-account-plus"></i> Student Registration</h2>
        </div>
        
        <!-- Search section -->
        <div class="search-container">
          <div class="search-wrapper">
            <i class="mdi mdi-magnify search-icon"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search student..."
              class="search-input"
              @input="handleSearch"
            />
          </div>
          
          <div v-if="searchResults.length > 0" class="search-results">
            <div 
              v-for="student in searchResults" 
              :key="student.id"
              class="search-result-item"
              @click="selectStudent(student)"
            >
              <div class="student-info">
                <div class="student-name">{{ student.name || 'No name' }}</div>
                <div class="student-email">{{ student.email || 'No email' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Fields -->
        <div class="form-fields">
          <div class="form-group">
            <i class="mdi mdi-id-card"></i>
            <input type="text" v-model="studentId" placeholder="Student ID" readonly>
          </div>
          
          <div class="form-group">
            <i class="mdi mdi-account"></i>
            <input type="text" v-model="fullName" placeholder="Full Name" readonly>
          </div>

          <div class="form-group">
            <i class="mdi mdi-email"></i>
            <input type="email" v-model="email" placeholder="Email" readonly>
          </div>
        </div>

        <!-- Captured Images -->
        <div class="captured-images" v-if="capturedImages.length">
          <h3>Captured Photos ({{ capturedImages.length }}/3)</h3>
          <div class="image-grid">
            <div v-for="(image, index) in capturedImages" :key="index" class="image-item">
              <img :src="image" alt="Captured face">
              <button @click="removeImage(index)" class="remove-btn">
                <i class="mdi mdi-close"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Success/Error Dialog -->
        <div v-if="dialog.show" class="dialog-overlay" @click="closeDialog">
          <div class="dialog" @click.stop>
            <div class="dialog-content">
              <i :class="['mdi', dialog.isSuccess ? 'mdi-check-circle success-icon' : 'mdi-alert error-icon']"></i>
              <h3>{{ dialog.message }}</h3>
              <button @click="closeDialog" class="dialog-btn">
                OK
              </button>
            </div>
          </div>
        </div>

        <button 
          @click="submitRegistration" 
          :disabled="!canSubmit || isRegistering" 
          class="submit-btn"
        >
          <template v-if="!isRegistering">
            <i class="mdi mdi-check-circle"></i>
            Register Student
          </template>
          <template v-else>
            <i class="mdi mdi-loading mdi-spin"></i>
            Registering...
          </template>
        </button>
      </div>

      <!-- Right Panel -->
      <div class="right-panel">
        <div class="camera-section">
          <div class="camera-container">
            <div class="video-wrapper">
              <!-- Camera Frame -->
              <div class="camera-frame">
                <div class="frame-header">ALIGN FACE WITHIN FRAME</div>
                <div class="frame-corners">
                  <div class="corner top-left"></div>
                  <div class="corner top-right"></div>
                  <div class="corner bottom-left"></div>
                  <div class="corner bottom-right"></div>
                </div>
                <div class="frame-guide"></div>
              </div>
              
              <video ref="video" autoplay playsinline></video>
              <canvas ref="canvas" style="display:none"></canvas>
              
              <div class="camera-overlay" v-if="!isCameraStarted">
                <button @click="startCamera" class="camera-btn">
                  <i class="mdi mdi-camera"></i> Start Camera
                </button>
              </div>
              
              <div class="camera-controls" v-if="isCameraStarted">
                <button 
                  @click="captureImage" 
                  :disabled="capturedImages.length >= 3 || isProcessing"
                  class="capture-btn"
                >
                  <i class="mdi mdi-camera" v-if="!isProcessing"></i>
                  <i class="mdi mdi-loading mdi-spin" v-else></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegistrationView',
  data() {
    return {
      studentId: '',
      fullName: '',
      email: '',
      stream: null,
      capturedImages: [],
      isCameraStarted: false,
      error: null,
      searchQuery: '',
      searchResults: [],
      showResults: false,
      debounceTimer: null,
      isProcessing: false,
      isRegistering: false,
      dialog: {
        show: false,
        message: '',
        isSuccess: true
      }
    }
  },
  computed: {
    canSubmit() {
      return this.studentId && 
             this.fullName && 
             this.capturedImages.length >= 3;
    }
  },
  methods: {
    async startCamera() {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'user',
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });
        
        const video = this.$refs.video;
        video.srcObject = this.stream;
        video.onloadedmetadata = () => {
          video.play();
          this.isCameraStarted = true;
        };
      } catch (err) {
        console.error('Error accessing camera:', err);
        this.error = 'Unable to access camera. Please ensure you have granted camera permissions.';
        alert(this.error);
      }
    },
    
    async optimizeImage(imageData, size = 280, quality = 0.8) {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = imageData;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw the image as is (already flipped during capture)
          ctx.drawImage(img, 0, 0, size, size);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
      });
    },

    async captureImage() {
      if (this.capturedImages.length >= 3) {
        alert('Maximum of 3 images allowed');
        return;
      }

      try {
        this.isProcessing = true;
        const video = this.$refs.video;
        const canvas = this.$refs.canvas;
        
        const guideSize = 280;
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        const centerX = (videoWidth - guideSize) / 2;
        const centerY = (videoHeight - guideSize) / 2;
        
        canvas.width = guideSize;
        canvas.height = guideSize;
        
        const context = canvas.getContext('2d');
        
        // Save the current context state
        context.save();
        
        // Flip the context horizontally to match the video display
        context.scale(-1, 1);
        context.translate(-guideSize, 0);
        
        // Draw the image flipped (same as display)
        context.drawImage(
          video,
          centerX, centerY,
          guideSize, guideSize,
          0, 0,
          guideSize, guideSize
        );
        
        // Restore the context
        context.restore();
        
        const rawImageData = canvas.toDataURL('image/jpeg', 1.0);
        const optimizedImage = await this.optimizeImage(rawImageData, 280, 0.8);
        
        this.capturedImages.push(optimizedImage);
        this.showCaptureEffect();
        
        const imageSize = Math.round((optimizedImage.length * 3) / 4);
        console.log(`Captured image size: ${(imageSize / 1024).toFixed(2)}KB`);
        
      } catch (error) {
        console.error('Error capturing image:', error);
        alert('Failed to capture image. Please try again.');
      } finally {
        this.isProcessing = false;
      }
    },

    validateImageSize(imageData) {
      const sizeInBytes = Math.round((imageData.length * 3) / 4);
      const sizeInMB = sizeInBytes / (1024 * 1024);
      
      // Warn if image is too large (e.g., > 1MB)
      if (sizeInMB > 1) {
        console.warn(`Large image detected: ${sizeInMB.toFixed(2)}MB`);
        return false;
      }
      return true;
    },

    removeImage(index) {
      this.capturedImages.splice(index, 1);
    },

    showCaptureEffect() {
      // Add a flash effect or sound here if desired
      const flash = document.createElement('div');
      flash.className = 'camera-flash';
      this.$refs.video.parentElement.appendChild(flash);
      setTimeout(() => flash.remove(), 150);
    },

    showDialog(message, isSuccess = true) {
      this.dialog = {
        show: true,
        message,
        isSuccess
      };
    },

    closeDialog() {
      this.dialog.show = false;
      if (this.dialog.isSuccess) {
        this.resetForm();
      }
    },

    async submitRegistration() {
      if (this.isRegistering) return;
      
      try {
        this.isRegistering = true;
        
        const optimizedImages = await Promise.all(
          this.capturedImages.map(img => 
            this.validateImageSize(img) ? img : this.optimizeImage(img, 280, 0.7)
          )
        );

        const requestData = {
          studentId: this.studentId,
          fullName: this.fullName,
          email: this.email,
          images: optimizedImages
        };

        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        const data = await response.json();

        if (response.ok) {
          // Update to handle returned S3 URLs if needed
          this.showDialog('Registration successful!', true);
          
          // Optional: Display the S3 URLs in the captured images section
          if (data.student && data.student.faceUrls) {
            // You might want to update the UI to show the S3 URLs
            console.log('Face URLs:', data.student.faceUrls);
          }
        } else {
          throw new Error(data.error || 'Registration failed');
        }
      } catch (err) {
        console.error('Error during registration:', err);
        this.showDialog(err.message || 'Registration failed. Please try again.', false);
      } finally {
        this.isRegistering = false;
      }
    },

    resetForm() {
      this.studentId = '';
      this.fullName = '';
      this.email = '';
      this.capturedImages = [];
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
        this.isCameraStarted = false;
      }
    },

    handleSearch() {
      clearTimeout(this.debounceTimer);
      
      if (this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }

      this.debounceTimer = setTimeout(async () => {
        try {
          const response = await fetch(
            `${process.env.VUE_APP_API_EDCLOCK}/api/v1/edclock/get-all-students?search=${encodeURIComponent(this.searchQuery)}`
          );
          
          if (!response.ok) {
            throw new Error('Search failed');
          }

          const data = await response.json();
          
          if (data && data.data) {
            this.searchResults = data.data.slice(0, 5); // Limit to 5 results
            console.log('Search Results:', this.searchResults); // For debugging
          }
        } catch (error) {
          console.error('Error searching students:', error);
          this.searchResults = [];
        }
      }, 300);
    },

    selectStudent(student) {
      this.studentId = student.id || '';
      this.fullName = student.name || '';
      this.email = student.email || '';
      this.searchQuery = student.name || '';
      this.searchResults = []; // Clear results after selection
    },

    // Add click outside handler to close search results
    handleClickOutside(event) {
      if (!event.target.closest('.search-container')) {
        this.showResults = false;
      }
    },

    async fetchStudentDetails(studentId) {
      try {
        const response = await fetch(`${process.env.VUE_APP_API_EDCLOCK}/api/v1/edclock/get-student-detail?student_id=${studentId}`);
        const data = await response.json();
        
        if (data && data.data) {
          const student = data.data;
          // Fill the form with fetched student data
          this.studentId = student.id || '';
          this.fullName = student.name || '';
          this.email = student.email || '';
          this.searchQuery = student.name || ''; // Update search query as well
        } else {
          console.error('Student details not found');
          alert('Student details not found');
        }
      } catch (error) {
        console.error('Error fetching student details:', error);
        alert('Error fetching student details');
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    
    // Get student_id from query parameter and fetch details
    const studentId = this.$route.query.student_id;
    if (studentId) {
      this.fetchStudentDetails(studentId);
    }
  },
  beforeUnmount() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style scoped>
.registration {
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.registration-container {
  width: 90%;
  max-width: 1400px;
  height: 85vh;
  display: flex;
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Left Panel Styles */
.left-panel {
  width: 35%;
  min-width: 400px;
  background: white;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: auto;
  border-right: 1px solid #e5e7eb;
  position: relative;
}

.header h2 {
  color: #1e293b;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Search Styles */
.search-container {
  position: relative;
  width: 100%;
  z-index: 1000; /* Ensure dropdown appears above other elements */
}

.search-wrapper {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-input {
  width: 100%;
  padding: 12px 12px 12px 45px;
  background: #f1f5f9;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  background: white;
  border-color: #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* Form Fields Styles */
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
}

.form-group {
  position: relative;
  width: 100%;
}

.form-group i {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.form-group input {
  width: 100%;
  padding: 12px 12px 12px 45px;
  background: #f1f5f9;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
}

.form-group input[readonly] {
  background: #f1f5f9;
  cursor: not-allowed;
}

/* Right Panel Styles */
.right-panel {
  flex: 1;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.camera-section {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.camera-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.video-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
  -webkit-transform: scaleX(-1);
}

/* Camera Frame Styles */
.camera-frame {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 10;
}

.frame-header {
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;
  text-align: center;
  color: #42b983;
  font-size: 0.8rem;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(66, 185, 131, 0.5);
}

.frame-corners {
  position: absolute;
  inset: 40px;
}

.corner {
  position: absolute;
  width: 50px;
  height: 50px;
  border: 3px solid #42b983;
  box-shadow: 0 0 10px rgba(66, 185, 131, 0.3);
}

.top-left {
  top: 0;
  left: 0;
  border-right: none;
  border-bottom: none;
}

.top-right {
  top: 0;
  right: 0;
  border-left: none;
  border-bottom: none;
}

.bottom-left {
  bottom: 0;
  left: 0;
  border-right: none;
  border-top: none;
}

.bottom-right {
  bottom: 0;
  right: 0;
  border-left: none;
  border-top: none;
}

.frame-guide {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 280px;
  height: 280px;
  border: 3px dashed rgba(66, 185, 131, 0.8);
  border-radius: 12px;
  box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.3);
  background-image: 
    linear-gradient(rgba(66, 185, 131, 0.2) 1px, transparent 1px),
    linear-gradient(90deg, rgba(66, 185, 131, 0.2) 1px, transparent 1px);
  background-size: 70px 70px;
  background-position: center center;
}

.frame-guide::after {
  content: 'Center your face in the square';
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(66, 185, 131, 1);
  font-size: 1.1rem;
  white-space: nowrap;
  font-weight: 500;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  background: rgba(0, 0, 0, 0.4);
  padding: 8px 16px;
  border-radius: 6px;
  letter-spacing: 0.5px;
}

/* Camera Controls */
.camera-controls {
  position: absolute;
  bottom: 30px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 20;
}

.capture-btn {
  width: 70px;
  height: 70px;
  background: rgba(66, 185, 131, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.capture-btn:hover:not(:disabled) {
  transform: scale(1.1);
}

.capture-btn i {
  font-size: 2rem;
  color: white;
}

.camera-btn {
  background: rgba(66, 185, 131, 0.9);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.camera-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  z-index: 30;
}

/* Captured Images Styles */
.captured-images h3 {
  color: #1e293b;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.image-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Submit Button */
.submit-btn {
  margin-top: auto;
  background: #42b983;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 12px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 48px; /* Add this to prevent size change during loading */
}

.submit-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.submit-btn:hover:not(:disabled) {
  background: #3aa876;
  transform: translateY(-1px);
}

/* Search Results Dropdown */
.search-results {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
}

.search-result-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f1f5f9;
}

.search-result-item:hover {
  background-color: #f8fafc;
}

.search-result-item:last-child {
  border-bottom: none;
}

.student-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.student-name {
  color: #1e293b;
  font-weight: 500;
}

.student-email {
  color: #64748b;
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .registration-container {
    width: 95%;
  }

  .left-panel {
    min-width: 350px;
  }
}

/* Add loading animation styles */
.mdi-spin {
  animation: spin 1s infinite linear;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Update capture button styles for loading state */
.capture-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Add these new styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.2s ease;
}

.dialog {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.3s ease;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.5rem;
}

.success-icon {
  font-size: 4rem;
  color: #42b983;
}

.error-icon {
  font-size: 4rem;
  color: #ef4444;
}

.dialog h3 {
  color: #1e293b;
  font-size: 1.2rem;
  margin: 0;
}

.dialog-btn {
  background: #42b983;
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dialog-btn:hover {
  background: #3aa876;
  transform: translateY(-1px);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style> 